import { useCallback, useEffect, useRef, useState } from "react";
import { RecordingStatusContext, RecordingStatusContextType } from "./RecordingStatusContext";

import { channel, sendRecordingStatus, sendRequestStatus } from "@/broadcasting/recording";
import { BroadcastEventEnum, RecordEventEnum } from "@/types/recording";
import { useLocation, useParams } from "react-router-dom";
import { getRecordingDataFromDB } from "@/utils/indexedDBUtils";
import { RecordStatusEnum } from "@/pages/RecordsList/types";

export interface RecordingStatusProviderProps {
  children: React.ReactNode;
}

export function RecordingStatusProvider({ children }: RecordingStatusProviderProps) {
  const [isRecording, setIsRecording] = useState(false);

  const { pathname } = useLocation();

  const { recordingUUID } = useParams();

  const recordingStatusRef = useRef(RecordEventEnum.IDLE);

  /**
   * True while this tab is waiting to find out whether another tab already owns a recording
   * screen. Set right before asking other tabs for their status and cleared either once a
   * reply confirms someone else got there first, or after a short grace period with no reply.
   */
  const awaitingDuplicateScreenCheckRef = useRef(false);

  const handleRequestStatus = useCallback(() => {
    if (pathname.includes("recording")) {
      sendRecordingStatus(RecordEventEnum.RECORDING);
    }
  }, [pathname]);

  const handleRecordEvent = useCallback(
    (ev: MessageEvent) => {
      const { event, type } = ev.data;
      if (event !== BroadcastEventEnum.RECORDING) return;
      switch (type) {
        case RecordEventEnum.REQUEST_STATUS:
          handleRequestStatus();
          break;
        case RecordEventEnum.RECORDING:
          setIsRecording(true);
          if (awaitingDuplicateScreenCheckRef.current && pathname.includes("recording")) {
            awaitingDuplicateScreenCheckRef.current = false;
            window.location.replace(window.location.origin);
          }
          break;
        case RecordEventEnum.IDLE:
          setIsRecording(false);
          break;
        default:
          break;
      }
    },
    [handleRequestStatus, pathname],
  );

  const handleChangePathname = useCallback(() => {
    const isOnRecordingPage = pathname.includes("recording");
    if (isOnRecordingPage) {
      recordingStatusRef.current = RecordEventEnum.RECORDING;
      sendRecordingStatus(RecordEventEnum.RECORDING);
    } else if (recordingStatusRef.current === RecordEventEnum.RECORDING) {
      recordingStatusRef.current = RecordEventEnum.IDLE;
      sendRecordingStatus(RecordEventEnum.IDLE);
      setIsRecording(false);
    }
  }, [pathname]);

  const handlePageHide = useCallback(
    (event: PageTransitionEvent) => {
      const isClosingTab = !event.persisted;
      const isOnRecordingPage = pathname.includes("recording");
      if (isClosingTab && isOnRecordingPage) {
        sendRecordingStatus(RecordEventEnum.IDLE);
      }
    },
    [pathname],
  );

  /**
   * Prevents render the recording page if the start pathname is the RecordingPage
   */
  const handleDuplicatedRecordingTab = useCallback(async () => {
    const isOnRecordingPage = window.location.pathname.includes("recording");
    if (isOnRecordingPage && recordingUUID) {
      const recording = await getRecordingDataFromDB(recordingUUID);
      const allowedEntryStatus = [RecordStatusEnum.PAUSED, RecordStatusEnum.NONE, RecordStatusEnum.READY_FOR_RECORDING];
      if (recording?.status && !allowedEntryStatus.includes(recording?.status)) {
        window.location.replace(window.location.origin);
      }
      return;
    }
  }, [recordingUUID]);

  useEffect(() => {
    handleChangePathname();

    window.addEventListener("pagehide", handlePageHide);
    channel.addEventListener("message", handleRecordEvent);

    return () => {
      channel.removeEventListener("message", handleRecordEvent);
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, [handleChangePathname, handleRecordEvent, handlePageHide]);

  useEffect(() => {
    handleDuplicatedRecordingTab();

    if (channel) {
      awaitingDuplicateScreenCheckRef.current = pathname.includes("recording");
      sendRequestStatus();
    }

    const clearDuplicateScreenCheck = setTimeout(() => {
      awaitingDuplicateScreenCheckRef.current = false;
    }, 500);

    return () => clearTimeout(clearDuplicateScreenCheck);
  }, [handleDuplicatedRecordingTab, pathname]);

  const value: RecordingStatusContextType = {
    isRecording,
  };

  return <RecordingStatusContext value={value}>{children}</RecordingStatusContext>;
}
